import { AgeBand, City, Gender, MAX_ATHLETES } from "@fsl/shared";
import type { AthleteDraft, SurveyState } from "./types.js";

const CITIES: ReadonlyArray<{ value: City; label: string }> = [
  { value: City.EAGLE_MOUNTAIN,    label: "Eagle Mountain" },
  { value: City.SARATOGA_SPRINGS,  label: "Saratoga Springs" },
  { value: City.LEHI,              label: "Lehi" },
  { value: City.OTHER,             label: "Somewhere else" },
];

const AGE_BANDS: ReadonlyArray<{ value: AgeBand; label: string }> = [
  { value: AgeBand.SEVEN_TO_NINE,       label: "7–9" },
  { value: AgeBand.TEN_TO_ELEVEN,       label: "10–11" },
  { value: AgeBand.TWELVE_TO_THIRTEEN,  label: "12–13" },
];

const GENDERS: ReadonlyArray<{ value: Gender; label: string }> = [
  { value: Gender.MALE,              label: "Boy" },
  { value: Gender.FEMALE,            label: "Girl" },
  { value: Gender.NON_BINARY,        label: "Non-binary" },
  { value: Gender.PREFER_NOT_TO_SAY, label: "Prefer not to say" },
];

function resizeAthletes(current: AthleteDraft[], n: number): AthleteDraft[] {
  if (n === current.length) return current;
  if (n < current.length) return current.slice(0, n);
  const next = [...current];
  while (next.length < n) next.push({ ageBand: "", gender: "" });
  return next;
}

interface Props {
  state: SurveyState;
  patch: (p: Partial<SurveyState>) => void;
  canSubmit: boolean;
  submitting: boolean;
}

export function Step3({ state, patch, canSubmit, submitting }: Props): JSX.Element {
  const setAthlete = (i: number, a: Partial<AthleteDraft>): void => {
    const athletes = state.athletes.map((x, idx) => (idx === i ? { ...x, ...a } : x));
    patch({ athletes });
  };

  const setNumKids = (raw: string): void => {
    const parsed = parseInt(raw, 10);
    const n = Math.max(1, Math.min(MAX_ATHLETES, Number.isFinite(parsed) ? parsed : 1));
    patch({ numKids: n, athletes: resizeAthletes(state.athletes, n) });
  };

  return (
    <div className="step active" data-step="3">
      <div className="field">
        <label htmlFor="parent_name">Your name</label>
        <input
          type="text"
          id="parent_name"
          name="parent_name"
          placeholder="First and last"
          value={state.parentName}
          onChange={(e) => patch({ parentName: e.target.value })}
        />
      </div>

      <div className="field">
        <label htmlFor="parent_email">Your email</label>
        <input
          type="email"
          id="parent_email"
          name="parent_email"
          placeholder="you@email.com"
          value={state.parentEmail}
          onChange={(e) => patch({ parentEmail: e.target.value })}
        />
      </div>

      <div className="field">
        <label htmlFor="city">Your city</label>
        <select
          id="city"
          name="city"
          value={state.city}
          onChange={(e) => patch({ city: e.target.value as City | "" })}
        >
          <option value="">Select a city</option>
          {CITIES.map((c) => (
            <option key={c.value} value={c.value}>{c.label}</option>
          ))}
        </select>
      </div>

      <div className="field">
        <label htmlFor="num_kids">How many athletes would you be signing up?</label>
        <input
          type="number"
          id="num_kids"
          name="num_kids"
          min={1}
          max={MAX_ATHLETES}
          value={state.numKids}
          onChange={(e) => setNumKids(e.target.value)}
        />
      </div>

      <div id="athletes-list">
        {state.athletes.map((a, i) => (
          <div key={i} className="athlete-row">
            <div className="row-label">Athlete {i + 1}</div>
            <select
              name={`athlete_${i + 1}_age`}
              value={a.ageBand}
              onChange={(e) => setAthlete(i, { ageBand: e.target.value as AgeBand | "" })}
            >
              <option value="">Age band</option>
              {AGE_BANDS.map((b) => (
                <option key={b.value} value={b.value}>{b.label}</option>
              ))}
            </select>
            <select
              name={`athlete_${i + 1}_gender`}
              value={a.gender}
              onChange={(e) => setAthlete(i, { gender: e.target.value as Gender | "" })}
            >
              <option value="">Gender</option>
              {GENDERS.map((g) => (
                <option key={g.value} value={g.value}>{g.label}</option>
              ))}
            </select>
          </div>
        ))}
      </div>

      <div className="consent">
        <input
          type="checkbox"
          id="consent"
          name="consent"
          checked={state.consent}
          onChange={(e) => patch({ consent: e.target.checked })}
        />
        <label htmlFor="consent">
          I agree to FSL contacting me about my Fall 2026 spot. We'll keep you in the loop
          as we build the league, sharing progress on fields, coach hires, and timing
          along the way. No spam, no selling your email, no nonsense.
        </label>
      </div>

      <div className="form-actions">
        <div></div>
        <div className="right-side">
          <button type="submit" className="btn btn-primary" disabled={!canSubmit}>
            {submitting ? "Signing up…" : "Signup for Fall 2026 →"}
          </button>
        </div>
      </div>
    </div>
  );
}
